import React, { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { aiService } from '../services/aiService';
import { scrapingService } from '../services/scrapingService';
import { useData } from '../contexts/DataContext';
import DomainSelector from '../components/shared/DomainSelector';
import StreamingContainer from '../components/CategoryDescription/StreamingContainer';
import GeneratedContent from '../components/ProductDescription/GeneratedContent';
import { useTranslation } from 'react-i18next';
import StatusMessage from '../components/CategoryDescription/StatusMessage';
import StreamingModal from '../components/shared/StreamingModal';
import { useNotification } from '../contexts/NotificationContext';
import { Domain } from '../types/data';
import { extractErrorMessageFromResponse } from '../services/errorService';
import { CustomerGroupSelector } from '../components/shared/CustomerGroupSelector';
import { BrandSelector } from '../components/shared/BrandSelector';
import FeatureFlagOverlay from '../components/shared/FeatureFlagOverlay';
import { FeatureFlag } from '../utils/featureFlags';

const defaultPromptLanguage = 'English';
const defaultPromptToneOfVoice = 'Professional';

const PRODUCT_SECTIONS = [
  { key: 'titles', title: 'titleRecommendations', isTextSection: true },
  { key: 'meta', title: 'metaDescriptionRecommendations', isTextSection: true },
  { key: 'h1', title: 'h1Recommendations', isTextSection: true },
  { key: 'description', title: 'productDescription', isTextSection: false },
  { key: 'brand', title: 'brandInformation', isTextSection: false },
  { key: 'category', title: 'categoryInformation', isTextSection: false },
];

const getIntensityColor = (level: number) => {
  const colors: { [key: number]: string } = {
    0: 'bg-white dark:bg-white',
    1: 'bg-green-50 dark:bg-green-900',
    2: 'bg-green-100 dark:bg-green-800',
    3: 'bg-green-200 dark:bg-green-700',
    4: 'bg-green-300 dark:bg-green-600',
    5: 'bg-green-400 dark:bg-green-500',
  };
  return colors[level] || colors[0];
};

const ProductDescription: React.FC = () => {
  const {
    domains,
    targetCompany,
    customerTargetGroups,
    fetchRelatedDomainData,
    brands,
    fetchDomains,
  } = useData();
  const [activeDomain, setActiveDomain] = useState<Domain | null>(null);
  const [selectedCustomerGroup, setSelectedCustomerGroup] = useState('');
  const [productUrl, setProductUrl] = useState('');
  const [categoryUrl, setCategoryUrl] = useState('');
  const [brandTextAmount, setBrandTextAmount] = useState(0);
  const [categoryTextAmount, setCategoryTextAmount] = useState(0);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [productData, setProductData] = useState<any>(null);
  const [categoryData, setCategoryData] = useState<any>(null);
  const [generatedContent, setGeneratedContent] = useState<Record<string, null | string>>(
    PRODUCT_SECTIONS.reduce(
      (acc, section) => ({
        ...acc,
        [section.key]: null,
      }),
      {}
    )
  );
  const [isGenerating, setIsGenerating] = useState(false);
  const [streamingStatus, setStreamingStatus] = useState<Record<string, string>>({});
  const { t } = useTranslation();
  const [isFetching, setIsFetching] = useState(false);
  const [fetchStatus, setFetchStatus] = useState({
    product: 'Waiting...',
    category: 'Waiting...',
  });
  const { showNotification } = useNotification();

  useEffect(() => {
    fetchDomains();
  }, []);

  useEffect(() => {
    loadData();
  }, [domains, targetCompany]);

  const loadData = () => {
    if (targetCompany && domains.length > 0) {
      const foundDomain = domains.find((domain) => domain.company_id === targetCompany);
      if (foundDomain) {
        setActiveDomain(() => foundDomain);
        fetchRelatedDomainData(foundDomain.id);
      } else {
        setActiveDomain(null);
      }
    }
  };

  const fetchUrlData = async () => {
    const trimmedProductUrl = productUrl.trim();
    const trimmedCategoryUrl = categoryUrl.trim();

    if (!trimmedProductUrl) {
      showNotification('Please enter a product URL', 'error');
      return;
    }

    setIsFetching(true);
    setFetchStatus({
      product: 'Fetching...',
      category: trimmedCategoryUrl ? 'Fetching...' : 'Waiting...',
    });

    try {
      // Fetch product data
      const { data: productResponse, error } =
        await scrapingService.fetchProductData(trimmedProductUrl);
      if (error) {
        throw new Error(await extractErrorMessageFromResponse(error, t));
      }
      setProductData(productResponse);
      setFetchStatus((prev) => ({ ...prev, product: 'Complete' }));

      // Fetch category data if URL provided
      if (trimmedCategoryUrl) {
        const { data: categoryResponse, error: categoryError } =
          await scrapingService.fetchCategoryData(trimmedCategoryUrl);
        if (categoryError) {
          throw new Error(await extractErrorMessageFromResponse(categoryError, t));
        }
        setCategoryData(categoryResponse);
        setFetchStatus((prev) => ({ ...prev, category: 'Complete' }));
      } else {
        setFetchStatus((prev) => ({ ...prev, category: 'Skipped' }));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      Sentry.captureException(error);
      setFetchStatus({
        product: 'Error',
        category: trimmedCategoryUrl ? 'Error' : 'Skipped',
      });
      const errorMessage =
        error instanceof Error ? error.message : 'Error fetching data. Please try again.';

      showNotification(errorMessage, 'error');
    } finally {
      // Add a small delay before closing the modal
      setTimeout(() => {
        setIsFetching(false);
      }, 1000);
    }
  };

  const handleGenerateContent = async () => {
    if (!productUrl || !selectedCustomerGroup) {
      showNotification('Please enter a URL and select a customer group', 'info');
      return;
    }

    // Initialize sections based on configuration
    const sections = PRODUCT_SECTIONS.reduce(
      (acc, section) => {
        // Add section if:
        // - It's a brand section and brandTextAmount > 0
        // - It's a category section and categoryTextAmount > 0
        // - It's any other section
        if (
          (section.key === 'brand' && brandTextAmount > 0) ||
          (section.key === 'category' && categoryTextAmount > 0) ||
          (section.key !== 'brand' && section.key !== 'category')
        ) {
          acc[section.key] = null;
        }
        return acc;
      },
      {} as Record<string, null | string>
    );

    setIsGenerating(true);
    setGeneratedContent(sections);

    try {
      // Create array of sections to generate
      const allSections = Object.keys(sections);
      // Process sections in batches
      const processInBatches = async (sections: string[], batchSize: number) => {
        for (let i = 0; i < sections.length; i += batchSize) {
          const batch = sections.slice(i, i + batchSize);
          await Promise.all(batch.map((section) => generateSection(section)));
        }
      };

      await processInBatches(allSections, 5);
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error generating content:', error);
      showNotification('Error generating content. Please try again.', 'error');
    } finally {
      setIsGenerating(false);
    }
  };

  const generateSection = async (section: string) => {
    setStreamingStatus((prev) => ({ ...prev, [section]: 'generating' }));

    try {
      const customerGroup = customerTargetGroups.find((g) => g.name === selectedCustomerGroup);
      const selectedBrandInfo = brands?.find((b) => b.id.toString() === selectedBrand);

      const { data: response, error } = await aiService.generateProductDescriptions({
        language: activeDomain?.language || defaultPromptLanguage,
        toneOfVoice: activeDomain?.tone_of_voice || defaultPromptToneOfVoice,
        customerGroupDescription: customerGroup?.description || '',
        domainName: activeDomain?.domain || '',
        categoryDescription: categoryData?.description || '',
        brandDescription: selectedBrandInfo?.description || '',
        productDetails: productData,
        brandAmount: brandTextAmount,
        categoryAmount: categoryTextAmount,
        brandTextAmount: getBrandTextAmount(brandTextAmount),
        categoryTextAmount: getCategoryTextAmount(categoryTextAmount),
        brandToneOfVoice: undefined,
        count: section === 'meta' || section === 'titles' || section === 'h1' ? 3 : 1,
        section,
        companyId: targetCompany ?? '',
      });
      if (error) {
        throw new Error(await extractErrorMessageFromResponse(error, t));
      }

      if (!response.body) {
        throw new Error('No response body received');
      }

      const decoder = new TextDecoder();
      let accumulatedContent = '';

      const reader = response.body.getReader();

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                accumulatedContent += data.content;

                setGeneratedContent((prev) => ({
                  ...prev,
                  [section]: accumulatedContent,
                }));
              } catch (e) {
                console.error('Error parsing SSE data:', e);
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }

      setStreamingStatus((prev) => ({ ...prev, [section]: 'completed' }));
    } catch (error) {
      Sentry.captureException(error);
      console.error(`Error generating ${section}:`, error);
      setStreamingStatus((prev) => ({ ...prev, [section]: 'error' }));
      showNotification(
        error instanceof Error ? error.message : 'Error generating content. Please try again.',
        'error'
      );
      throw error;
    }
  };

  // Helper functions to get text descriptions
  const getBrandTextAmount = (amount: number): string => {
    switch (amount) {
      case 0:
        return t('product.brand_text_amounts.0');
      case 1:
        return t('product.brand_text_amounts.1');
      case 2:
        return t('product.brand_text_amounts.2');
      case 3:
        return t('product.brand_text_amounts.3');
      case 4:
        return t('product.brand_text_amounts.4');
      case 5:
        return t('product.brand_text_amounts.5');
      default:
        return t('product.brand_text_amounts.3');
    }
  };

  const getCategoryTextAmount = (amount: number): string => {
    switch (amount) {
      case 0:
        return t('product.category_text_amounts.0');
      case 1:
        return t('product.category_text_amounts.1');
      case 2:
        return t('product.category_text_amounts.2');
      case 3:
        return t('product.category_text_amounts.3');
      case 4:
        return t('product.category_text_amounts.4');
      case 5:
        return t('product.category_text_amounts.5');
      default:
        return t('product.category_text_amounts.3');
    }
  };

  // Check for targetCompany and activeDomain before rendering the main content
  if (!targetCompany || !activeDomain) {
    return <StatusMessage targetCompany={targetCompany} activeDomain={activeDomain} />;
  }

  return (
    <FeatureFlagOverlay feature={FeatureFlag.PRODUCT_DESCRIPTION}>
      <div className="p-8">
        <h1 className="text-3xl font-bold mb-2">{t('product.product_descriptions')}</h1>
        <div className="mb-6 mt-4">
          {/* Domain Selector */}
          <DomainSelector activeDomain={activeDomain} setActiveDomain={setActiveDomain} />
        </div>

        {/* URL Inputs */}
        <div className="space-y-4 rounded-lg mb-6">
          <div>
            <label className="block text-gray-700">{t('product.product_url')}:</label>
            <input
              type="url"
              value={productUrl}
              onChange={(e) => setProductUrl(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border rounded-md"
              placeholder={t('product.enter_product_url')}
            />
          </div>

          <div>
            <label className="block text-gray-700">
              {t('product.category_url')}:{' '}
              <span className="text-sm text-gray-500">({t('common.optional')})</span>
            </label>
            <input
              type="url"
              value={categoryUrl}
              onChange={(e) => setCategoryUrl(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border rounded-md"
              placeholder={t('product.enter_category_url')}
            />
          </div>

          <button
            onClick={fetchUrlData}
            className="w-full bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            {t('product.fetch_data')}
          </button>
        </div>

        {/* Display Fetched Information and Configuration Options only after fetching */}
        {productData && (
          <>
            {/* Display Fetched Information */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="p-4 border rounded-lg bg-white dark:bg-gray-800">
                <h3 className="font-semibold mb-2">{t('product.product_information')}</h3>
                <div className="space-y-2">
                  <p>
                    <strong>{t('common.title')}:</strong> {productData?.title || 'N/A'}
                  </p>
                  <p>
                    <strong>H1:</strong> {productData?.h1 || 'N/A'}
                  </p>
                  <p>
                    <strong>{t('common.meta_description')}:</strong>{' '}
                    {productData?.metaDescription || 'N/A'}
                  </p>
                </div>
              </div>

              {categoryData && (
                <div className="p-4 border rounded-lg bg-white dark:bg-gray-800">
                  <h3 className="font-semibold mb-2">{t('product.category_information')}</h3>
                  <div className="space-y-2">
                    <p>
                      <strong>{t('common.title')}:</strong> {categoryData?.title || 'N/A'}
                    </p>
                    <p>
                      <strong>H1:</strong> {categoryData?.h1 || 'N/A'}
                    </p>
                    <p>
                      <strong>{t('common.meta_description')}:</strong>{' '}
                      {categoryData?.metaDescription || 'N/A'}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Configuration Options */}
            <div className="space-y-6">
              {/* Customer Group Selection */}
              <div className="p-4 border rounded-lg bg-white dark:bg-gray-800">
                <CustomerGroupSelector
                  customerGroups={customerTargetGroups}
                  selectedGroup={selectedCustomerGroup}
                  onSelect={setSelectedCustomerGroup}
                />
              </div>

              {/* Brand Configuration */}
              <div className="p-4 border rounded-lg bg-white dark:bg-gray-800">
                <h3 className="text-lg font-medium mb-4">{t('product.brand_configuration')}</h3>

                {/* Brand Selection */}
                <div className="mb-4">
                  <BrandSelector
                    brands={brands || []}
                    selectedBrand={selectedBrand}
                    onSelect={setSelectedBrand}
                  />
                </div>

                {/* Brand Text Amount */}
                <div
                  className={`p-4 rounded-lg shadow transition-colors duration-200 ${getIntensityColor(brandTextAmount)}`}
                >
                  <div className="flex items-center justify-between">
                    <label className="font-medium text-gray-700 dark:text-gray-200">
                      {t('product.brand_text_amount')}
                    </label>
                    <span className="text-sm text-gray-500">{brandTextAmount}</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="5"
                    value={brandTextAmount}
                    onChange={(e) => setBrandTextAmount(parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-300 rounded-lg appearance-none cursor-pointer dark:bg-gray-700 mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-2">
                    <span>{t('common.none')}</span>
                    <span>{t('common.full')}</span>
                  </div>
                  <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    {getBrandTextAmount(brandTextAmount)}
                  </div>
                </div>
              </div>

              {/* Category Configuration */}
              {categoryData && (
                <div className="p-4 border rounded-lg bg-white dark:bg-gray-800">
                  <h3 className="text-lg font-medium mb-4">
                    {t('product.category_configuration')}
                  </h3>
                  <div
                    className={`p-4 rounded-lg shadow transition-colors duration-200 ${getIntensityColor(categoryTextAmount)}`}
                  >
                    <div className="flex items-center justify-between">
                      <label className="font-medium text-gray-700 dark:text-gray-200">
                        {t('product.amount_of_category_information')}
                      </label>
                      <span className="text-sm text-gray-500">{categoryTextAmount}</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="5"
                      value={categoryTextAmount}
                      onChange={(e) => setCategoryTextAmount(parseInt(e.target.value))}
                      className="w-full h-2 bg-gray-300 rounded-lg appearance-none cursor-pointer dark:bg-gray-700 mt-2"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-2">
                      <span>{t('common.none')}</span>
                      <span>{t('common.full')}</span>
                    </div>
                    <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                      {getCategoryTextAmount(categoryTextAmount)}
                    </div>
                  </div>
                </div>
              )}

              {/* Generate Button */}
              <button
                onClick={handleGenerateContent}
                disabled={isGenerating || !selectedCustomerGroup || !selectedBrand}
                className="w-full bg-blue-500 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isGenerating ? t('common.generating') : t('product.generate_product_content')}
              </button>
            </div>
          </>
        )}

        {/* Streaming Status */}
        {isGenerating && (
          <StreamingContainer status={streamingStatus} generatedContent={generatedContent} />
        )}

        {/* Generated Content Display */}
        {Object.values(generatedContent).some((content) => content !== null) && (
          <GeneratedContent content={generatedContent} sections={PRODUCT_SECTIONS} />
        )}

        {/* Fetching Product and Category Modal */}
        {isFetching && (
          <StreamingModal
            productStatus={fetchStatus.product}
            categoryStatus={fetchStatus.category}
          />
        )}
      </div>
    </FeatureFlagOverlay>
  );
};

export default ProductDescription;
